import { Button, Dialog, DialogActions, DialogContent, DialogTitle, Typography } from '@mui/material';
import { useCallback, useEffect, useRef, useState } from 'react';
import { isFeedLinkActive } from '../../lib/feed-link-activity';
import { checkIndexedDbHealth } from '../../lib/idb-health';

// Intervalo entre verificações de saúde enquanto o app está aberto.
const CHECK_INTERVAL_MS = 15_000;
// Tempo até o reinício automático após detectar a falha.
const RESTART_COUNTDOWN_SECONDS = 30;
// Falhas consecutivas necessárias para acusar corrupção.
const FAILURES_TO_TRIP = 3;

/**
 * Monitora a integridade do IndexedDB no boot e em intervalo regular.
 *
 * Se o IndexedDB não carregar/estiver corrompido (falhas consecutivas, não uma falha isolada), exibe um aviso
 * bloqueante com contagem regressiva de 30s e um botão; ambos reiniciam o app (reinício limpo, pois o main encerra os
 * serviços antes de relançar).
 */
export function IdbGuard() {
  const [failed, setFailed] = useState(false);
  const [secondsLeft, setSecondsLeft] = useState(RESTART_COUNTDOWN_SECONDS);

  const restartedRef = useRef(false);
  const failureCountRef = useRef(0);

  const restart = useCallback(() => {
    if (restartedRef.current) return;
    restartedRef.current = true;
    window.electronAPI.restartApp();
  }, []);

  // Verificação periódica de saúde (boot + a cada CHECK_INTERVAL_MS).
  useEffect(() => {
    let active = true;
    let checking = false;

    const run = async () => {
      // Sob carga do Feed Link a latência sobe sem o banco estar corrompido: medir agora só daria falso positivo
      if (checking || isFeedLinkActive()) return;
      checking = true;

      try {
        const healthy = await checkIndexedDbHealth();
        if (!active) return;

        if (healthy) {
          failureCountRef.current = 0;
          return;
        }

        failureCountRef.current += 1;
        if (failureCountRef.current >= FAILURES_TO_TRIP) {
          setFailed(true);
        }
      } finally {
        checking = false;
      }
    };

    run();
    const interval = setInterval(run, CHECK_INTERVAL_MS);

    return () => {
      active = false;
      clearInterval(interval);
    };
  }, []);

  // Contagem regressiva para reinício automático após detectar a falha.
  useEffect(() => {
    if (!failed) return;

    const interval = setInterval(() => {
      setSecondsLeft((prev) => {
        if (prev <= 1) {
          clearInterval(interval);
          restart();
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(interval);
  }, [failed, restart]);

  if (!failed) return null;

  return (
    <Dialog
      open
      disableEscapeKeyDown
      // Bloqueante: não fecha ao clicar fora.
      onClose={() => {}}
      aria-labelledby="idb-guard-title"
    >
      <DialogTitle id="idb-guard-title">Erro inesperado</DialogTitle>
      <DialogContent>
        <Typography>aconteceu um erro inesperado, o Feed Manager ira precisar ser reiniciado</Typography>
        <Typography sx={{ mt: 2 }} color="text.secondary">
          Reiniciando automaticamente em {secondsLeft}s...
        </Typography>
      </DialogContent>
      <DialogActions>
        <Button variant="contained" color="error" onClick={restart}>
          Reiniciar agora
        </Button>
      </DialogActions>
    </Dialog>
  );
}
